import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { SegmentFormValues } from "../schemas";

import { Badge } from "@/components/ui/v2/badge";
import { Input } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";
import { SEGMENT_STATUS_CONFIG, type SegmentStatus } from "@/features/segments/constants";

type SegmentNameFieldProps = {
  readOnly: boolean;
  status?: SegmentStatus;
  autoFocus: boolean;
};

const NAME_INPUT_ID = "segment-name";
const NAME_ERROR_ID = "segment-name-error";

export function SegmentNameField({ readOnly, status, autoFocus }: SegmentNameFieldProps) {
  const { t } = useTranslation();
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<SegmentFormValues>();

  const statusBadge = status && (
    <Badge variant={SEGMENT_STATUS_CONFIG[status].variant} size="small">
      {t(SEGMENT_STATUS_CONFIG[status].labelKey)}
    </Badge>
  );

  if (readOnly) {
    return (
      <div className="flex items-center gap-3">
        <h2 className="min-w-0 text-lg font-semibold [overflow-wrap:anywhere]">{watch("name")}</h2>
        {statusBadge}
      </div>
    );
  }

  return (
    <div className="grid gap-2 md:max-w-xl">
      <Label htmlFor={NAME_INPUT_ID}>
        {t("common.name")}
        <span aria-hidden className="text-destructive">
          *
        </span>
      </Label>
      <div className="flex items-center gap-3">
        <Input
          id={NAME_INPUT_ID}
          autoFocus={autoFocus}
          placeholder={t("admin.segments.segmentName")}
          aria-required
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? NAME_ERROR_ID : undefined}
          {...register("name")}
        />
        {statusBadge}
      </div>
      {errors.name?.message && (
        <p id={NAME_ERROR_ID} className="text-sm text-destructive">
          {errors.name.message}
        </p>
      )}
    </div>
  );
}
